import { getApiBaseUrl as getHttpApiBaseUrl } from '../utils/axios';
import { getFreshToken } from '../utils/tokenUtils';

// Determine API base URL for WebSocket connections using the same source as Axios
const getWsBaseUrl = () => {
  // getHttpApiBaseUrl() returns something like "http://host:port/api"
  const httpApiBase = getHttpApiBaseUrl();
  const httpOrigin = httpApiBase.replace(/\/?api$/, '');
  // Convert http/https to ws/wss
  const wsOrigin = httpOrigin.replace(/^http/, 'ws');
  
  // Debug logging for WebSocket URL construction
  console.log('🔍 WebSocket URL construction:', {
    httpApiBase,
    httpOrigin,
    wsOrigin,
    isDev: import.meta.env.DEV
  });
  
  return wsOrigin;
};

export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp: string;
  project_id?: string;
}

export interface WebSocketCallbacks {
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onReconnect?: () => void;
  onReconnectAttempt?: (attempt: number) => void;
}

export class DevAgentWebSocket {
  private ws: WebSocket | null = null;
  private threadId: string;
  private token: string;
  private callbacks: WebSocketCallbacks;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = Infinity; // Keep trying indefinitely
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 60000; // Max 60 seconds between attempts
  private isConnecting = false;
  private shouldReconnect = true;
  private reconnectResetInterval: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private networkStatusListener: (() => void) | null = null;
  private pongTimeout: NodeJS.Timeout | null = null;
  private lastPong = Date.now();
  private lastThreadClosedAt: number | null = null;
  // We keep local lastSeq but do not request replay from server
  private lastSeq: number = 0;
  private hasConnectedBefore = false;
  private visibilityChangeListener: (() => void) | null = null;
  private isPageVisible = true;
  private disconnectionReason: string = '';
  private consecutivePingFailures = 0;
  private maxPingFailures = 3;
  private consecutiveQuickFailures = 0;
  private lastConnectTime = 0;

  constructor(threadId: string, token: string, callbacks: WebSocketCallbacks = {}) {
    this.threadId = threadId;
    this.token = token;
    this.callbacks = callbacks;
  }

  updateCallbacks(callbacks: WebSocketCallbacks) {
    this.callbacks = callbacks;
  }

  updateToken(token: string): void {
    this.token = token;
  }

  async connect(): Promise<void> {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isConnecting = true;

    try {
      const baseUrl = getWsBaseUrl();
      const wsUrl = `${baseUrl}/api/dev-agent/ws/${this.threadId}?token=${this.token}`;
      
      console.log('🔌 Attempting WebSocket connection:', {
        baseUrl,
        wsUrl,
        threadId: this.threadId,
        tokenLength: this.token.length,
        tokenPreview: this.token.substring(0, 20) + '...'
      });
      
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('🔌 WebSocket connected for thread:', this.threadId);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.consecutiveQuickFailures = 0;
        this.lastConnectTime = Date.now();
        this.stopReconnectResetInterval();
        this.startPingPong();
        this.setupNetworkStatusListener();
        this.setupVisibilityListener();
        
        // Call appropriate callback based on connection history
        if (this.hasConnectedBefore) {
          console.log('🔄 WebSocket reconnected successfully for thread:', this.threadId);
          this.callbacks.onReconnect?.();
        } else {
          console.log('✅ WebSocket initial connection established for thread:', this.threadId);
          this.hasConnectedBefore = true;
          this.callbacks.onConnect?.();
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log('📨 Raw WebSocket message received for thread:', this.threadId, 'Type:', message.type, 'Full message:', message);
          
          // Handle pong messages
          if (message.type === 'pong') {
            this.lastPong = Date.now();
            this.consecutivePingFailures = 0; // Reset failure count on successful pong
            console.log('🏓 Pong received successfully');
            if (this.pongTimeout) {
              clearTimeout(this.pongTimeout);
              this.pongTimeout = null;
            }
            return;
          }

          // Server informs that this thread was closed/deleted
          if (message.type === 'thread_closed') {
            this.lastThreadClosedAt = Date.now();
            // Do not attempt reconnection on this instance; let UI decide new thread
            this.shouldReconnect = false;
            try { this.ws?.close(1000, 'Thread closed'); } catch {}
            return;
          }

          // Handle connection established
          if (message.type === 'connection_established') {
            console.log('✅ WebSocket connection established for thread:', this.threadId);
            return;
          }

          // Pass other messages to callback
          this.callbacks.onMessage?.(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        const reason = event.reason || this.disconnectionReason || 'Unknown reason';
        console.log('🔌 WebSocket disconnected for thread:', this.threadId, 'Code:', event.code, 'Reason:', reason);
        console.log('🔍 Close event details:', {
          code: event.code,
          reason: reason,
          wasClean: event.wasClean,
          type: event.type,
          isPageVisible: this.isPageVisible,
          consecutivePingFailures: this.consecutivePingFailures,
          lastPong: new Date(this.lastPong).toISOString()
        });
        
        this.isConnecting = false;
        this.stopPingPong();
        this.disconnectionReason = '';
        this.consecutivePingFailures = 0;
        this.callbacks.onDisconnect?.();

        // Detect rapid connection failures (connect → close in < 3 s)
        const timeSinceConnect = Date.now() - this.lastConnectTime;
        if (this.lastConnectTime > 0 && timeSinceConnect < 3000) {
          this.consecutiveQuickFailures++;
        } else {
          this.consecutiveQuickFailures = 0;
        }

        if (this.consecutiveQuickFailures >= 5) {
          console.error('❌ Too many rapid connection failures — likely auth issue. Pausing reconnection.');
          this.consecutiveQuickFailures = 0;
          return;
        }

        // Don't reconnect for authentication errors (4001-4003) or normal closure (1000)
        const isAuthError = event.code >= 4001 && event.code <= 4003;
        const isNormalClosure = event.code === 1000 && reason !== 'Ping/pong failure';
        
        if (!isAuthError && !isNormalClosure && this.shouldReconnect) {
          this.scheduleReconnect();
        } else if (isAuthError) {
          console.error('❌ Authentication error, stopping reconnection attempts:', reason);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error for thread:', this.threadId, error);
        console.log('🔍 Error event details:', {
          type: error.type,
          target: error.target,
          isTrusted: error.isTrusted
        });
        this.isConnecting = false;
        this.callbacks.onError?.(error);
        
        // Schedule reconnection on error if we should reconnect
        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.isConnecting = false;
      this.callbacks.onError?.(error as Event);
      
      // Schedule reconnection on creation error if we should reconnect
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 6)), this.maxReconnectDelay);
    
    console.log(`🔄 Scheduling WebSocket reconnect attempt ${this.reconnectAttempts} in ${delay}ms (will keep trying indefinitely)`);
    
    this.callbacks.onReconnectAttempt?.(this.reconnectAttempts);
    this.startReconnectResetInterval();
    
    setTimeout(async () => {
      if (!this.shouldReconnect) return;

      console.log(`🔄 Attempting WebSocket reconnection ${this.reconnectAttempts} for thread: ${this.threadId}`);

      try {
        const freshToken = await getFreshToken();
        if (freshToken) {
          this.token = freshToken;
        }
      } catch {
        const storedToken = localStorage.getItem('token') || '';
        if (storedToken) this.token = storedToken;
      }

      this.connect();
    }, delay);
  }

  private startReconnectResetInterval(): void {
    if (this.reconnectResetInterval) return;
    
    // Reset reconnection attempts every 10 minutes to prevent exponentially long delays
    this.reconnectResetInterval = setInterval(() => {
      if (this.reconnectAttempts > 0) {
        console.log(`🔄 Resetting reconnection attempts for thread ${this.threadId} to prevent excessive delays`);
        this.reconnectAttempts = Math.max(0, this.reconnectAttempts - 5); // Gradually reduce attempts
      }
    }, 10 * 60 * 1000); // 10 minutes
  }

  private stopReconnectResetInterval(): void {
    if (this.reconnectResetInterval) {
      clearInterval(this.reconnectResetInterval);
      this.reconnectResetInterval = null;
    }
  }

  private setupNetworkStatusListener(): void {
    if (this.networkStatusListener || typeof navigator === 'undefined') return;
    
    this.networkStatusListener = () => {
      console.log('🌐 Network connectivity restored, attempting immediate reconnection');
      if (this.shouldReconnect && (!this.ws || this.ws.readyState !== WebSocket.OPEN)) {
        // Reset attempts on network recovery for faster reconnection
        this.reconnectAttempts = 0;
        this.consecutivePingFailures = 0; // Reset ping failures on network recovery
        this.connect();
      }
    };
    
    window.addEventListener('online', this.networkStatusListener);
  }

  private removeNetworkStatusListener(): void {
    if (this.networkStatusListener) {
      window.removeEventListener('online', this.networkStatusListener);
      this.networkStatusListener = null;
    }
  }

  private setupVisibilityListener(): void {
    if (this.visibilityChangeListener || typeof document === 'undefined') return;
    
    this.visibilityChangeListener = () => {
      this.isPageVisible = !document.hidden;
      console.log(`👁️ Page visibility changed: ${this.isPageVisible ? 'visible' : 'hidden'}`);
      
      if (this.isPageVisible) {
        // Page became visible - reset ping failures and resume normal operation
        this.consecutivePingFailures = 0;
        console.log('👁️ Page became visible, resuming normal WebSocket operation');
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeListener);
  }

  private removeVisibilityListener(): void {
    if (this.visibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
      this.visibilityChangeListener = null;
    }
  }

  private startPingPong(): void {
    // Send ping every 30 seconds
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // Skip ping if page is not visible to avoid unnecessary disconnections
        if (!this.isPageVisible) {
          console.log('⏸️ Skipping ping while page is not visible');
          return;
        }
        this.sendPing();
      }
    }, 30000);
  }

  private stopPingPong(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private sendPing(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        const pingMessage = { 
          type: 'ping', 
          timestamp: new Date().toISOString() 
        };
        this.ws.send(JSON.stringify(pingMessage));
        console.log('🏓 Ping sent, waiting for pong...');
        
        // Set pong timeout (10 seconds - increased from 5 for better reliability)
        this.pongTimeout = setTimeout(() => {
          this.consecutivePingFailures++;
          console.warn(`⚠️ No pong received (failure ${this.consecutivePingFailures}/${this.maxPingFailures})`);
          
          // Only close connection after multiple consecutive failures
          if (this.consecutivePingFailures >= this.maxPingFailures) {
            console.error(`❌ Too many consecutive ping failures (${this.consecutivePingFailures}), closing connection`);
            this.disconnectionReason = 'Ping/pong failure';
            this.ws?.close(1000, 'Ping/pong failure');
          } else {
            console.log(`⚠️ Ping failure ${this.consecutivePingFailures}/${this.maxPingFailures}, continuing...`);
          }
        }, 10000); // Increased timeout to 10 seconds
      } catch (error) {
        console.error('❌ Error sending ping:', error);
        this.consecutivePingFailures++;
      }
    }
  }

  // No-op persistence methods retained for API stability; not used
  private persistLastSeq(): void {}
  private restoreLastSeq(): void {}

  sendMessage(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected, cannot send message');
    }
  }

  getStatus(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'get_status' }));
        
        // Set up one-time message handler for status response
        const originalOnMessage = this.callbacks.onMessage;
        this.callbacks.onMessage = (message) => {
          if (message.type === 'status_update') {
            this.callbacks.onMessage = originalOnMessage;
            resolve(message.data);
          } else {
            originalOnMessage?.(message);
          }
        };
        
        // Timeout after 5 seconds
        setTimeout(() => {
          this.callbacks.onMessage = originalOnMessage;
          reject(new Error('Status request timeout'));
        }, 5000);
      } else {
        reject(new Error('WebSocket is not connected'));
      }
    });
  }

  stopTask(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'stop_task' }));
        
        // Set up one-time message handler for stop response
        const originalOnMessage = this.callbacks.onMessage;
        this.callbacks.onMessage = (message) => {
          if (message.type === 'task_stopped' || message.type === 'error') {
            this.callbacks.onMessage = originalOnMessage;
            if (message.type === 'task_stopped') {
              resolve(message.data);
            } else {
              reject(new Error(message.data.message));
            }
          } else {
            originalOnMessage?.(message);
          }
        };
        
        // Timeout after 5 seconds
        setTimeout(() => {
          this.callbacks.onMessage = originalOnMessage;
          reject(new Error('Stop task request timeout'));
        }, 5000);
      } else {
        reject(new Error('WebSocket is not connected'));
      }
    });
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.stopPingPong();
    this.stopReconnectResetInterval();
    this.removeNetworkStatusListener();
    this.removeVisibilityListener();
    this.disconnectionReason = 'User disconnected';
    
    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  isOpenOrConnecting(): boolean {
    if (!this.ws) return false;
    return this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING;
  }

  getConnectionState(): string {
    if (!this.ws) return 'CLOSED';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }
}

// WebSocket manager to handle multiple connections
export class WebSocketManager {
  private connections: Map<string, DevAgentWebSocket> = new Map();

  connect(threadId: string, token: string, callbacks: WebSocketCallbacks = {}): DevAgentWebSocket {
    // If a connection exists and is open or connecting, reuse it and just update callbacks
    const existing = this.connections.get(threadId);
    if (existing && existing.isOpenOrConnecting()) {
      existing.updateCallbacks(callbacks);
      return existing;
    }

    // Otherwise, disconnect any stale connection and create a new one
    if (existing) {
      existing.disconnect();
      this.connections.delete(threadId);
    }

    const ws = new DevAgentWebSocket(threadId, token, callbacks);
    this.connections.set(threadId, ws);

    ws.connect().catch(error => {
      console.error('Failed to connect WebSocket for thread:', threadId, error);
    });

    return ws;
  }

  disconnect(threadId: string): void {
    const ws = this.connections.get(threadId);
    if (ws) {
      ws.disconnect();
      this.connections.delete(threadId);
    }
  }

  getConnection(threadId: string): DevAgentWebSocket | undefined {
    return this.connections.get(threadId);
  }

  disconnectAll(): void {
    this.connections.forEach((ws, threadId) => {
      ws.disconnect();
    });
    this.connections.clear();
  }

  getConnectionCount(): number {
    return this.connections.size;
  }

  getConnectionStates(): Record<string, string> {
    const states: Record<string, string> = {};
    this.connections.forEach((ws, threadId) => {
      states[threadId] = ws.getConnectionState();
    });
    return states;
  }
}

// Global WebSocket manager instance
export const websocketManager = new WebSocketManager(); 